// combine-normalize.js
// Gestion de la combinaison et normalisation des clips audio

import { convertToMp3, optimizedMp3Encode } from './audio-encoder.js';
import { normalizeAudioBuffer } from './audio-utils.js';

/**
 * Combine plusieurs clips audio en un seul
 */
export async function combineAudioClips(audioElements, combinedClipName, combinedClipsContainer, combineButtonContainer) {
  const progressContainer = document.createElement('div');
  const progressBar = document.createElement('div');
  progressContainer.className = 'progress-container active';
  progressBar.className = 'progress-bar';
  progressContainer.appendChild(progressBar);
  combineButtonContainer.appendChild(progressContainer);
  
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const mp3Blobs = [];

    // Conversion de chaque clip en MP3
    for (let i = 0; i < audioElements.length; i++) {
      const progress = (percent) => {
        const totalProgress = (i * 100 + percent) / audioElements.length;
        progressBar.style.width = `${totalProgress}%`;
      };
      
      const mp3Blob = await convertToMp3(audioElements[i], progress);
      mp3Blobs.push(mp3Blob);
    }

    // Concaténation des MP3
    const combinedBlob = new Blob(mp3Blobs, { type: 'audio/mp3' });
    const combinedUrl = URL.createObjectURL(combinedBlob);

    // Création du clip combiné
    createCombinedClip(combinedUrl, combinedClipName, combinedClipsContainer);

  } catch (error) {
    console.error('Erreur lors de la combinaison:', error);
    alert(`Erreur lors de la combinaison des clips: ${error.message}`);
  } finally {
    progressContainer.remove();
  }
}

/**
 * Combine et normalise plusieurs clips audio
 */
export async function combineAndNormalizeAudioClips(audioElements, combinedClipName, combinedClipsContainer, combineButtonContainer) {
  const progressContainer = document.createElement('div');
  const progressBar = document.createElement('div');
  progressContainer.className = 'progress-container active';
  progressBar.className = 'progress-bar';
  progressContainer.appendChild(progressBar);
  combineButtonContainer.appendChild(progressContainer);
  
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const audioBuffers = [];

    // Chargement et normalisation de chaque clip
    for (let i = 0; i < audioElements.length; i++) {
      const response = await fetch(audioElements[i].src);
      const arrayBuffer = await response.arrayBuffer();
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
      
      const normalizedBuffer = await normalizeAudioBuffer(audioContext, audioBuffer);
      audioBuffers.push(normalizedBuffer);
      
      progressBar.style.width = `${((i + 1) / audioElements.length) * 50}%`;
    }

    // Création du buffer final
    const totalLength = audioBuffers.reduce((sum, buffer) => sum + buffer.length, 0);
    const combinedBuffer = audioContext.createBuffer(
      1,
      totalLength,
      audioContext.sampleRate
    );

    // Combinaison des buffers normalisés
    let offset = 0;
    for (const buffer of audioBuffers) {
      const channelData = buffer.getChannelData(0);
      combinedBuffer.copyToChannel(channelData, 0, offset);
      offset += buffer.length;
    }

    // Conversion en MP3
    const offlineContext = new OfflineAudioContext(
      1,
      combinedBuffer.length,
      combinedBuffer.sampleRate
    );
    
    const source = offlineContext.createBufferSource();
    source.buffer = combinedBuffer;
    source.connect(offlineContext.destination);
    source.start();

    const renderedBuffer = await offlineContext.startRendering();
    const mp3Blob = await optimizedMp3Encode(
      renderedBuffer.getChannelData(0),
      renderedBuffer.sampleRate,
      1,
      (progress) => {
        progressBar.style.width = `${50 + progress / 2}%`;
      }
    );

    const combinedUrl = URL.createObjectURL(mp3Blob);
    createCombinedClip(combinedUrl, combinedClipName, combinedClipsContainer);

  } catch (error) {
    console.error('Erreur lors de la normalisation et combinaison:', error);
    alert(`Erreur lors de la normalisation: ${error.message}`);
  } finally {
    progressContainer.remove();
  }
}

/**
 * Crée un élément clip combiné dans le DOM
 */
function createCombinedClip(audioUrl, clipName, container) {
  const combinedClip = document.createElement('article');
  combinedClip.classList.add('clip');
  
  const combinedAudio = document.createElement('audio');
  combinedAudio.controls = true;
  combinedAudio.src = audioUrl;
  
  const combinedLabel = document.createElement('p');
  combinedLabel.textContent = clipName;
  
  const deleteButton = document.createElement('button');
  deleteButton.textContent = 'Supprimer';
  deleteButton.className = 'delete';
  
  const downloadButton = document.createElement('button');
  downloadButton.textContent = 'Télécharger';
  downloadButton.className = 'download';
  
  deleteButton.onclick = () => {
    combinedClip.remove();
    URL.revokeObjectURL(audioUrl);
  };
  
  downloadButton.onclick = () => {
    const a = document.createElement('a');
    a.href = audioUrl;
    a.download = `${clipName}.mp3`;
    a.click();
  };

  combinedClip.appendChild(combinedLabel);
  combinedClip.appendChild(combinedAudio);
  combinedClip.appendChild(deleteButton);
  combinedClip.appendChild(downloadButton);
  
  container.appendChild(combinedClip);
}